import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormControlElement} from 'Quoting/components/shared/QuoteFormControlElement';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getOptionFromValueTimePeriod, unitsOptions} from '../../PlanTemplates/ACC/benefitDefaults';
import {formatCurrencyValue} from './ACCBenefits';
import {AccidentFollowUpTreatment} from './AccidentFollowUpTreatment';
import {BrainInjuryDiagnosis} from './BrainInjuryDiagnosis';
import {BrokenTooth} from './BrokenTooth';
import {BurnDetails} from './BurnDetails';
import {BurnSkinGraft} from './BurnSkinGraft';
import {ComaDetails} from './ComaDetails';
import {LacerationDetails} from './LacerationDetails';
import {ParalysisDetails} from './ParalysisDetails';
import {accidentFacilityCarePath} from './PropertyPaths/accidentFacilityCarePath';
import {accidentInjuriesPath} from './PropertyPaths/accidentInjuriesPath';
import {getAccidentSurgeriesPath} from './PropertyPaths/accidentSurgeriesPath';
import {getTreatmentSupportAndRecovery} from './PropertyPaths/getTreatmentSupportAndRecovery';
import {accidentFollowupTreatmentPath, getAccidentDiagnosticPainPath} from './PropertyPaths/propertyPathACC';
import {RehabilitationFacility} from './RehabilitationFacility';

const MiscellaneousOutpatientSurgery = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);
  const {selectedPlan} = state.quoteForm;
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'miscellaneous-outpatient-surgery-row-1'},
      items: [
        {
          key: 'miscellaneous-outpatient-surgery-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              disabled
              required
              value={getQuoteFieldValue<number>(propertyPaths.miscellaneousOutpatientSurgeryDetails.incurralPeriod)}
              id={'miscellaneousOutpatientSurgeryDetails.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'miscellaneous-outpatient-surgery-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              adornment="money"
              disabled
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.miscellaneousOutpatientSurgeryDetails.benefitAmount)
              )}
              id={'miscellaneousOutpatientSurgeryDetails.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'miscellaneous-outpatient-surgery-row-2'},
      items: [
        {
          key: 'miscellaneous-outpatient-surgery-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCMiscellaneousOutpatientSurgeryBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.miscellaneousOutpatientSurgeryDetails.maxBenefitNumber)}
              disabled
              required
              id={'miscellaneousOutpatientSurgeryDetails.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'miscellaneous-outpatient-surgery-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCMiscellaneousOutpatientSurgeryBenefit.timePeriodCd`}
              label="Time Period"
              required
              value={getQuoteFieldValue<number>(propertyPaths.miscellaneousOutpatientSurgeryDetails.timePeriodCd)}
              disabled
              id={'accMiscellaneous.timePeriodCd'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const OpenAbdominalOrThoracicSurgery = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);

  const {selectedPlan} = state.quoteForm;
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'open-abdominal-thoracic-surgery-row-1'},
      items: [
        {
          key: 'open-abdominal-thoracic-surgery-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              disabled
              required
              value={getQuoteFieldValue<number>(propertyPaths.openAbdominalOrThoracicSurgeryDetails.incurralPeriod)}
              id={'accAbdominThoratic.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'open-abdominal-thoracic-surgery-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              required
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.openAbdominalOrThoracicSurgeryDetails.benefitAmount)
              )}
              disabled
              id={'accAbdominThoratic.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'open-abdominal-thoracic-surgery-row-2'},
      items: [
        {
          key: 'open-abdominal-thoracic-surgery-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCOpenAbdominalOrThoracicSurgeryBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.openAbdominalOrThoracicSurgeryDetails.maxBenefitNumber)}
              disabled
              required
              id={'accAbdominThoratic.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'open-abdominal-thoracic-surgery-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCXRayBenefit.timePeriodCd`}
              label="Time Period"
              required
              value={getQuoteFieldValue<number>(propertyPaths.openAbdominalOrThoracicSurgeryDetails.timePeriodCd)}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const LigamentRotatorcuffTendonKneecartilage = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);

  const {selectedPlan} = state.quoteForm;
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'ligament-rotatorcuff-tendon-kneecartilage-row-1'},
      items: [
        {
          key: 'ligament-rotatorcuff-tendon-kneecartilage-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              required
              value={getQuoteFieldValue<number>(propertyPaths.tendonLigamentRotatorCuffDetails.incurralPeriod)}
              disabled
              id={'accLigament.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'ligament-rotatorcuff-tendon-kneecartilage-without-repair',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Without Repair"
              adornment="money"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.tendonLigamentRotatorCuffDetails.withoutRepairAmount)
              )}
              disabled
              id={'accLigament.withoutRepairAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'ligament-rotatorcuff-tendon-kneecartilage-row-2'},
      items: [
        {
          key: 'ligament-rotatorcuff-tendon-kneecartilage-with-repair',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="With Repair"
              adornment="money"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.tendonLigamentRotatorCuffDetails.withRepairAmount)
              )}
              disabled
              id={'accLigament.withRepairAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'ligament-rotatorcuff-tendon-kneecartilage-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCTendonLigamentRotatorCuffBenefit.maxBenefitNumber`}
              required
              value={getQuoteFieldValue<number>(propertyPaths.tendonLigamentRotatorCuffDetails.maxBenefitNumber)}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'ligament-rotatorcuff-tendon-kneecartilage-row-2'},
      items: [
        {
          key: 'ligament-rotatorcuff-tendon-kneecartilage-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCTendonLigamentRotatorCuffBenefit.timePeriodCd`}
              label="Time Period"
              required
              value={getQuoteFieldValue<number>(propertyPaths.tendonLigamentRotatorCuffDetails.timePeriodCd)}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const GeneralAnesthesia = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);

  const {selectedPlan} = state.quoteForm;
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'general-anesthesia-row-1'},
      items: [
        {
          key: 'general-anesthesia-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              required
              value={getQuoteFieldValue<number>(propertyPaths.generalAnesthesiaDetails.incurralPeriod)}
              disabled
              id={'accAnesthesia.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'general-anesthesia-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              required
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.generalAnesthesiaDetails.benefitAmount)
              )}
              disabled
              id={'accAnesthesia.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'general-anesthesia-row-2'},
      items: [
        {
          key: 'general-anesthesia-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCGeneralAnesthesiaBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.generalAnesthesiaDetails.maxBenefitNumber)}
              disabled
              required
              id={'accAnesthesia.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'general-anesthesia-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCXRayBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.generalAnesthesiaDetails.timePeriodCd)}
              disabled
              required
              id={'accAnesthesia.timePeriodCd'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const RupturedDiscSurgery = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);

  const {selectedPlan} = state.quoteForm;
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'ruptured-or-herniated-disc-surgery-row-1'},
      items: [
        {
          key: 'ruptured-or-herniated-disc-surgery-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.rupturedDiscDetails.incurralPeriod)}
              disabled
              required
              id={'accDiscSurgery.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'ruptured-or-herniated-disc-surgery-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              adornment="money"
              required
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.rupturedDiscDetails.benefitAmount))}
              disabled
              id={'accDiscSurgery.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'ruptured-or-herniated-disc-surgery-row-2'},
      items: [
        {
          key: 'ruptured-or-herniated-disc-surgery-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCRupturedDiscBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.rupturedDiscDetails.maxBenefitNumber)}
              disabled
              required
              id={'accDiscSurgery.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'ruptured-or-herniated-disc-surgery-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCRupturedDiscBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.rupturedDiscDetails.timePeriodCd)}
              disabled
              required
              id={'accDiscSurgery.timePeriodCd'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const BloodAndPlasma = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);

  const {selectedPlan} = state.quoteForm;
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'blood-plasma-platelets-row-1'},
      items: [
        {
          key: 'blood-plasma-platelets-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.bloodPlasmaPlateletsDetails.incurralPeriod)}
              disabled
              required
              id={'accBlood.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'blood-plasma-platelets-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              required
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.bloodPlasmaPlateletsDetails.benefitAmount)
              )}
              disabled
              id={'accBlood.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'blood-plasma-platelets-row-2'},
      items: [
        {
          key: 'blood-plasma-platelets-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCBloodPlasmaPlateletsBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.bloodPlasmaPlateletsDetails.maxBenefitNumber)}
              disabled
              required
              id={'accBlood.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'blood-plasma-platelets-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCBloodPlasmaPlateletsBenefit.timePeriodCd`}
              label="Time Period"
              disabled
              required
              value={getQuoteFieldValue<number>(propertyPaths.bloodPlasmaPlateletsDetails.timePeriodCd)}
              id={'accBlood.timePeriodCd'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const EyeInjury = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);

  const {selectedPlan} = state.quoteForm;
  const propertyPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'eye-injury-row-1'},
      items: [
        {
          key: 'eye-injury-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.eyeInjuryDetails.incurralPeriod)}
              disabled
              required
              id={'accEye.incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'eye-injury-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.eyeInjuryDetails.benefitAmount))}
              disabled
              required
              adornment="money"
              id={'accEye.benefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'eye-injury-row-2'},
      items: [
        {
          key: 'eye-injury-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCEyeInjuryBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.eyeInjuryDetails.maxBenefitNumber)}
              disabled
              required
              id={'accEye.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'eye-injury-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCEyeInjuryBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.eyeInjuryDetails.timePeriodCd)}
              disabled
              required
              id={'accEye.timePeriodCd'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const CtScanAndMri = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);

  const {selectedPlan} = state.quoteForm;
  const propertyPaths = getAccidentDiagnosticPainPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'major-diagnostic-exam-row-1'},
      items: [
        {
          key: 'major-diagnostic-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.majorDiagnosticExamDetails.incurralPeriod)}
              id={'accDiagnostic.incurralPeriod'}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'major-diagnostic-exam-row-2'},
      items: [
        {
          key: 'major-diagnostic-customize-benefit-1',
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              disabled
              id={'accDiagnostic.cATCustomizeBenefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'major-diagnostic-ct-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Computed Axial Tomography (CAT/CT) Scan - Benefit Amount"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.majorDiagnosticExamDetails.cATBenefitAmount)
              )}
              id={'accDiagnostic.cATBenefitAmount'}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'major-diagnostic-exam-row-4'},
      items: [
        {
          key: 'major-diagnostic-customize-benefit-3',
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              id={'accDiagnostic.mRICustomizeBenefitAmount'}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'major-diagnostic-mri-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Magnetic Resonance Imaging (MRI) - Benefit Amount"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.majorDiagnosticExamDetails.mRIBenefitAmount)
              )}
              id={'accDiagnostic.mRIBenefitAmount'}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'brain-injury-diagnosis-row-7'},
      items: [
        {
          key: 'major-diagnostic-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Major Diag. Exams Payable per Time Period"
              required
              searchKey={`ACCMajorDiagnosticExamBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<string>(propertyPaths.majorDiagnosticExamDetails.maxBenefitNumber)}
              id={'accDiagnostic.maxBenefitNumber'}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'major-diagnostic-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={getOptionFromValueTimePeriod(
                getQuoteFieldValue<string>(propertyPaths.majorDiagnosticExamDetails.timePeriodCd) ?? ''
              )}
              value={getQuoteFieldValue<string>(propertyPaths.majorDiagnosticExamDetails.timePeriodCd)}
              id={'accDiagnostic.timePeriodCd'}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const PainManagement = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const accPainManagement = getAccidentDiagnosticPainPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'pain-management-row-1'},
      items: [
        {
          key: 'pain-management-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              disabled
              value={getQuoteFieldValue<number>(accPainManagement.painManagementDetails.incurralPeriod)}
              id={accPainManagement.painManagementDetails.incurralPeriod}
              onChange={e =>
                updateQuoteFieldValue(accPainManagement.painManagementDetails.incurralPeriod, e.target.value)
              }
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'pain-management-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(accPainManagement.painManagementDetails.benefitAmount)
              )}
              id={accPainManagement.painManagementDetails.benefitAmount}
              onChange={e =>
                updateQuoteFieldValue(accPainManagement.painManagementDetails.benefitAmount, e.target.value)
              }
              required
              disabled
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'pain-management-row-2'},
      items: [
        {
          key: 'epidural-injection',
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Epidural Injection"
              disabled
              checked={getQuoteFieldValue<boolean>(accPainManagement.painManagementDetails.epiduralInjection)}
              id={accPainManagement.painManagementDetails.epiduralInjection}
              onChange={e =>
                updateQuoteFieldValue(accPainManagement.painManagementDetails.epiduralInjection, e.target.value)
              }
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'pain-management-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCPainManagementBenefit.maxBenefitNumber`}
              label="Number of Times Benefit is Payable per Time Period"
              disabled
              value={getQuoteFieldValue<string>(accPainManagement.painManagementDetails.maxBenefitNumber)}
              id={accPainManagement.painManagementDetails.maxBenefitNumber}
              onChange={e =>
                updateQuoteFieldValue(accPainManagement.painManagementDetails.maxBenefitNumber, e.target.value)
              }
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'pain-management-row-3'},
      items: [
        {
          key: 'pain-management-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={getOptionFromValueTimePeriod(
                getQuoteFieldValue<string>(accPainManagement.painManagementDetails.timePeriodCd) ?? ''
              )}
              value={getQuoteFieldValue<number>(accPainManagement.painManagementDetails.timePeriodCd)}
              id={accPainManagement.painManagementDetails.timePeriodCd}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const FamilyMemberLodging = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'companion-nonLocal-lodging-row-1'},
      items: [
        {
          key: 'incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.companionNonLocalLodgingDetails.incurralPeriod)}
              disabled
              required
              type="number"
              id={propertyPaths.companionNonLocalLodgingDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.companionNonLocalLodgingDetails.distanceToHospitalMoreThanMilesCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Distance to the Hospital More than (Miles)"
              searchKey={`ACCCompanionNonLocalLodgingBenefit.distanceToHospitalMoreThanMilesCd`}
              value={getQuoteFieldValue<number>(
                propertyPaths.companionNonLocalLodgingDetails.distanceToHospitalMoreThanMilesCd
              )}
              disabled
              required
              id={propertyPaths.companionNonLocalLodgingDetails.distanceToHospitalMoreThanMilesCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'companion-nonLocal-lodging-row-2'},
      items: [
        {
          key: propertyPaths.companionNonLocalLodgingDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Day)"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.companionNonLocalLodgingDetails.benefitAmount)
              )}
              disabled
              required
              type="number"
              id={propertyPaths.companionNonLocalLodgingDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.companionNonLocalLodgingDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Number of Days Per Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.companionNonLocalLodgingDetails.maxBenefitNumber)}
              disabled
              required
              type="number"
              id={propertyPaths.companionNonLocalLodgingDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'companion-nonLocal-lodging-row-3'},
      items: [
        {
          key: propertyPaths.companionNonLocalLodgingDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCCompanionNonLocalLodgingBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.companionNonLocalLodgingDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.companionNonLocalLodgingDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const Appliance = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'medical-equipment-row-1'},
      items: [
        {
          key: propertyPaths.medicalEquipmentDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.medicalEquipmentDetails.incurralPeriod)}
              disabled
              required
              type="number"
              id={propertyPaths.medicalEquipmentDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.medicalEquipmentDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.medicalEquipmentDetails.benefitAmount)
              )}
              disabled
              type="number"
              id={propertyPaths.medicalEquipmentDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'medical-equipment-row-2'},
      items: [
        {
          key: propertyPaths.medicalEquipmentDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCMedicalEquipmentBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.medicalEquipmentDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.medicalEquipmentDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              required
              searchKey={`ACCMedicalEquipmentBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.medicalEquipmentDetails.timePeriodCd)}
              disabled
              id={propertyPaths.medicalEquipmentDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const MedicalSupplies = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'medical-supplies-row-1'},
      items: [
        {
          key: propertyPaths.medicalSuppliesDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.medicalSuppliesDetails.incurralPeriod)}
              disabled
              required
              type="number"
              id={propertyPaths.medicalSuppliesDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.medicalSuppliesDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.medicalSuppliesDetails.benefitAmount)
              )}
              disabled
              required
              type="number"
              id={propertyPaths.medicalSuppliesDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'medical-supplies-row-2'},
      items: [
        {
          key: propertyPaths.medicalSuppliesDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCMedicalSuppliesBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.medicalSuppliesDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.medicalSuppliesDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.medicalSuppliesDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCMedicalSuppliesBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.medicalSuppliesDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.medicalSuppliesDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const Medication = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'medication-row-1'},
      items: [
        {
          key: propertyPaths.medicationDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.medicationDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.medicationDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.medicationDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.medicationDetails.benefitAmount))}
              disabled
              required
              id={propertyPaths.medicationDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'medication-row-2'},
      items: [
        {
          key: propertyPaths.medicationDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCMedicationBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.medicationDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.medicationDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCMedicationBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.medicationDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.medicationDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const NonLocalTransportation = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'non-local-transportation-row-1'},
      items: [
        {
          key: propertyPaths.nonLocalTransportationDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.nonLocalTransportationDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.nonLocalTransportationDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.nonLocalTransportationDetails.travelDistanceMoreThan,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Travel Distance More Than (Miles - One Way)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.nonLocalTransportationDetails.travelDistanceMoreThan)}
              disabled
              required
              id={propertyPaths.nonLocalTransportationDetails.travelDistanceMoreThan}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'non-local-transportation-2'},
      items: [
        {
          key: propertyPaths.nonLocalTransportationDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Round Trip)"
              type="number"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.nonLocalTransportationDetails.benefitAmount)
              )}
              disabled
              required
              id={propertyPaths.nonLocalTransportationDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'benefit-times',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCNonLocalTransportationBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.nonLocalTransportationDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.nonLocalTransportationDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'non-local-transportation-3'},
      items: [
        {
          key: 'time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCNonLocalTransportationBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.nonLocalTransportationDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.nonLocalTransportationDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const PostAccidentCommonCarrierTransportation = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'post-accident-common-carrier-transportation-row-1'},
      items: [
        {
          key: 'incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              required
              value={getQuoteFieldValue<number>(
                propertyPaths.postAccidentCommonCarrierTransportationDetails.incurralPeriod
              )}
              disabled
              id={propertyPaths.postAccidentCommonCarrierTransportationDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.postAccidentCommonCarrierTransportationDetails.travelDistanceMoreThan,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Travel Distance More Than (Miles - One Way)"
              type="number"
              required
              value={getQuoteFieldValue<number>(
                propertyPaths.postAccidentCommonCarrierTransportationDetails.travelDistanceMoreThan
              )}
              disabled
              id={propertyPaths.postAccidentCommonCarrierTransportationDetails.travelDistanceMoreThan}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'post-accident-common-carrier-transportation-row-2'},
      items: [
        {
          key: propertyPaths.postAccidentCommonCarrierTransportationDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.postAccidentCommonCarrierTransportationDetails.benefitAmount)
              )}
              disabled
              id={propertyPaths.postAccidentCommonCarrierTransportationDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.postAccidentCommonCarrierTransportationDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCPostAccidentCommonCarrierTransportationBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(
                propertyPaths.postAccidentCommonCarrierTransportationDetails.maxBenefitNumber
              )}
              disabled
              required
              id={propertyPaths.postAccidentCommonCarrierTransportationDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'post-accident-common-carrier-transportation-row-3'},
      items: [
        {
          key: propertyPaths.postAccidentCommonCarrierTransportationDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              required
              searchKey={`ACCPostAccidentCommonCarrierTransportationBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(
                propertyPaths.postAccidentCommonCarrierTransportationDetails.timePeriodCd
              )}
              disabled
              id={propertyPaths.postAccidentCommonCarrierTransportationDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const Prosthesis = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'prosthesis-row-1'},
      items: [
        {
          key: propertyPaths.prosthesisDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.prosthesisDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.prosthesisDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'one-device',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="One Device"
              type="number"
              required
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.prosthesisDetails.oneDeviceAmount))}
              disabled
              id={propertyPaths.prosthesisDetails.oneDeviceAmount}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'prosthesis-row-2'},
      items: [
        {
          key: propertyPaths.prosthesisDetails.twoOrMoreDevicesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Two or More Devices"
              type="number"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.prosthesisDetails.twoOrMoreDevicesAmount)
              )}
              disabled
              id={propertyPaths.prosthesisDetails.twoOrMoreDevicesAmount}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.prosthesisDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCProsthesisBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.prosthesisDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.prosthesisDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'prosthesis-row-3'},
      items: [
        {
          key: propertyPaths.prosthesisDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              required
              searchKey={`ACCProsthesisBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.prosthesisDetails.timePeriodCd)}
              disabled
              id={propertyPaths.prosthesisDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const ResidenceOrAutomobileModification = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'residence-automobile-modification-row-1'},
      items: [
        {
          key: propertyPaths.residenceAutomobileModificationDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.residenceAutomobileModificationDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.residenceAutomobileModificationDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.residenceAutomobileModificationDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.residenceAutomobileModificationDetails.benefitAmount)
              )}
              disabled
              id={propertyPaths.residenceAutomobileModificationDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'residence-automobile-modification-row-2'},
      items: [
        {
          key: propertyPaths.residenceAutomobileModificationDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCResidenceAutomobileModificationBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.residenceAutomobileModificationDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.residenceAutomobileModificationDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.residenceAutomobileModificationDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCResidenceAutomobileModificationBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.residenceAutomobileModificationDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.residenceAutomobileModificationDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const Therapy = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'therapy-row-1'},
      items: [
        {
          key: propertyPaths.therapyDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.therapyDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.therapyDetails.incurralPeriod}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.therapyDetails.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Day)"
              type="number"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.therapyDetails.benefitAmount))}
              disabled
              required
              id={propertyPaths.therapyDetails.benefitAmount}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'therapy-row-2'},
      items: [
        {
          key: propertyPaths.therapyDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCTherapyBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.therapyDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.therapyDetails.maxBenefitNumber}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'therapy-row-3'},
      items: [
        {
          key: propertyPaths.therapyDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCTherapyBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.therapyDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.therapyDetails.timePeriodCd}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const BenefitEnhancementRiderBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);
  const {selectedPlan} = state.quoteForm;
  const situsStateCd = getQuoteFieldValue<string>('riskStateCd');
  const standardBenefitStates = ['CO', 'DC', 'MD', 'MN', 'RI', 'WA'];

  const accidentInjuriesPaths = accidentInjuriesPath(selectedPlan?.planIndex || 0, 0);
  const accidentFacilityCarePaths = accidentFacilityCarePath(selectedPlan?.planIndex || 0, 0);
  const accidentFollowupPaths = accidentFollowupTreatmentPath(selectedPlan?.planIndex || 0, 0);
  const accidentSurgeriesPaths = getAccidentSurgeriesPath(selectedPlan?.planIndex || 0, 0);
  const majorDiagnosticPaths = getAccidentDiagnosticPainPath(selectedPlan?.planIndex || 0, 0);
  const treatmentPaths = getTreatmentSupportAndRecovery(selectedPlan?.planIndex ?? 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'benefit-enhancement-units-row-1'},
      items: [
        {
          key: 'units',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Units"
              type="number"
              options={unitsOptions}
              value={getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries)}
              id={accidentInjuriesPaths.unitsOfAccidentInjuries}
              required
              disabled
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: 'benefit-times',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCBenefitStructure.scheduleOfBenefitEnhancementRider`}
              label="Schedule"
              value={getQuoteFieldValue<number>(accidentInjuriesPaths.schedule)}
              id={accidentInjuriesPaths.schedule}
              onChange={e => updateQuoteFieldValue(accidentInjuriesPaths.schedule, e.target.value)}
              required
              disabled={standardBenefitStates.includes(situsStateCd)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];
  const benefitEnhancementRider: ConditionalSection[] = [
    {
      Component: BrainInjuryDiagnosis,
      key: 'BrainInjuryDiagnosis',
      label: 'BRAIN INJURY DIAGNOSIS',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentInjuriesPaths.isBrainInjuryDiagnosisApplied, isApplied);
      },
    },
    {
      Component: BrokenTooth,
      key: 'BrokenTooth',
      label: 'BROKEN TOOTH',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentInjuriesPaths.isBrokenToothApplied, isApplied);
      },
    },
    {
      Component: BurnDetails,
      key: 'BurnDetails',
      label: 'BURN',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentInjuriesPaths.isBurnApplied, isApplied);
      },
    },
    {
      Component: BurnSkinGraft,
      key: 'BurnSkinGraft',
      label: 'BURN SKIN GRAFT',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentInjuriesPaths.isBurnSkinGraftApplied, isApplied);
      },
    },
    {
      Component: ComaDetails,
      key: 'ComaDetails',
      label: 'COMA WITH RESPIRATORY ASSISTANCE',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentInjuriesPaths.isComaApplied, isApplied);
      },
    },
    {
      Component: LacerationDetails,
      key: 'LacerationDetails',
      label: 'LACERATION',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentInjuriesPaths.isLacerationApplied, isApplied);
      },
    },
    {
      Component: ParalysisDetails,
      key: 'ParalysisDetails',
      label: 'PARALYSIS',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentInjuriesPaths.isParalysisApplied, isApplied);
      },
    },
    {
      Component: RehabilitationFacility,
      key: 'RehabilitationFacility',
      label: 'REHABILITATION FACILITY',
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      disabled: true,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentFacilityCarePaths.isRehabilitationFacilityApplied, isApplied);
      },
    },
    {
      Component: AccidentFollowUpTreatment,
      key: 'AccidentFollowUpTreatment',
      label: 'ACCIDENT FOLLOW UP TREATMENT',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentFollowupPaths.isFollowUpTreatmentApplied, isApplied);
      },
    },
    {
      Component: MiscellaneousOutpatientSurgery,
      key: 'MiscellaneousOutoatientSurgery',
      label: 'MISCELLANEOUS OUTPATIENT SURGERY',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentSurgeriesPaths.isMiscellaneousOutpatientSurgeryApplied, isApplied);
      },
    },
    {
      Component: OpenAbdominalOrThoracicSurgery,
      key: 'OpenAbdominalOrThoracicSurgery',
      label: 'OPEN ABDOMINAL OR THORACIC SURGERY',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentSurgeriesPaths.isOpenAbdominalOrThoracicSurgeryApplied, isApplied);
      },
    },
    {
      Component: LigamentRotatorcuffTendonKneecartilage,
      key: 'LigamentRotatorcuffTendonKneecartilage',
      label: 'LIGAMENT, ROTATOR CUFF, TENDON, OR KNEE CARTILAGE',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentSurgeriesPaths.isTendonLigamentRotatorCuffApplied, isApplied);
      },
    },
    {
      Component: GeneralAnesthesia,
      key: 'GeneralAnesthesia',
      label: 'GENERAL ANESTHESIA',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentSurgeriesPaths.isGeneralAnesthesiaApplied, isApplied);
      },
    },
    {
      Component: RupturedDiscSurgery,
      key: 'RupturedDiscSurgery',
      label: 'RUPTURED DISC SURGERY',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentSurgeriesPaths.isRupturedDiscApplied, isApplied);
      },
    },
    {
      Component: BloodAndPlasma,
      key: 'BloodAndPlasma',
      label: 'BLOOD AND PLASMA',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentSurgeriesPaths.isBloodPlasmaPlateletsApplied, isApplied);
      },
    },
    {
      Component: EyeInjury,
      key: 'EyeInjury',
      label: 'EYE INJURY',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(accidentSurgeriesPaths.isEyeInjuryApplied, isApplied);
      },
    },
    {
      Component: CtScanAndMri,
      key: 'CtScanAndMri',
      label: 'COMPUTED TOMOGRAPHY (CT) SCAN AND MRI (First treatment within 30 days)',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(majorDiagnosticPaths.isMajorDiagnosticExamApplied, isApplied);
      },
    },
    {
      Component: PainManagement,
      key: 'PainManagement',
      label: 'PAIN MANAGEMENT',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(majorDiagnosticPaths.isPainManagementApplied, isApplied);
      },
    },
    {
      Component: FamilyMemberLodging,
      key: 'FamilyMemberLodging',
      label: 'Family Member LODGING',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(treatmentPaths.isCompanionNonLocalLodgingApplied, isApplied);
      },
    },
    {
      Component: Appliance,
      key: 'Appliance',
      label: 'Appliance',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(treatmentPaths.isMedicalEquipmentApplied, isApplied);
      },
    },
    {
      Component: MedicalSupplies,
      key: 'MedicalSupplies',
      label: 'MEDICAL SUPPLIES',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(treatmentPaths.isMedicalSuppliesApplied, isApplied);
      },
    },
    {
      Component: Medication,
      key: 'Medication',
      label: 'MEDICATION',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(treatmentPaths.isMedicationApplied, isApplied);
      },
    },
    {
      Component: NonLocalTransportation,
      key: 'NonLocalTransportation',
      label: 'NON-LOCAL TRANSPORTATION',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(treatmentPaths.isNonLocalTransportationApplied, isApplied);
      },
    },
    {
      Component: PostAccidentCommonCarrierTransportation,
      key: 'PostAccidentCommonCarrierTransportation',
      label: 'POST-ACCIDENT COMMON CARRIER TRANSPORTATION',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(treatmentPaths.isPostAccidentCommonCarrierTransportationApplied, isApplied);
      },
    },
    {
      Component: Prosthesis,
      key: 'Prosthesis',
      label: 'PROSTHESIS',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(treatmentPaths.isProsthesisApplied, isApplied);
      },
    },
    {
      Component: ResidenceOrAutomobileModification,
      key: 'ResidenceOrAutomobileModification',
      label: 'RESIDENCE OR AUTOMOBILE MODIFICATION (365 days after the accident)',
      disabled: true,
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(treatmentPaths.isResidenceAutomobileModificationApplied, isApplied);
      },
    },
    {
      Component: Therapy,
      key: 'Therapy',
      label: 'THERAPY',
      isApplied: (getQuoteFieldValue<number>(accidentInjuriesPaths.unitsOfAccidentInjuries) ?? 0) > 1,
      disabled: true,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(treatmentPaths.isTherapyApplied, isApplied);
      },
    },
  ];

  return (
    <FormWrapper title="Benefit Enhancement Rider" collapsible withSeparator>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
        <QuoteFormConditionalSection sections={benefitEnhancementRider} />
      </Grid>
    </FormWrapper>
  );
};
